import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  computeDayStates,
  eventDayOf,
  summarizeDay,
  toJstDateString,
  type BlockedReason,
  type CheckinKind,
  type CheckinRecord,
  type CheckinSource,
  type DayStatus,
  type EventDay,
  type KioskExhibitor,
} from '@nt-tokyo/shared';
import { api } from '@/lib/api';

// 受付端末のデータ層。
//
// 回線が落ちても受付を止めないため、
// - 出展者一覧と受付記録は起動時とその後 1 分ごとにまとめて取得し、判定は手元で行う
// - 受付の記録は端末で UUID を振って localStorage にため、送れたら消す
//   (サーバは同じ id を二重に入れないので、送り直しても問題ない)
// という作りにしている。

export interface KioskBootstrap {
  eventYear: number;
  eventDates: { day1: string; day2: string | null } | null;
  serverNow: string;
  account: { id: string; email: string; role: string | null };
  exhibitors: KioskExhibitor[];
  records: CheckinRecord[];
  dayStatuses: DayStatus[];
}

export interface PendingRecord {
  id: string;
  exhibitorId: string;
  kind: CheckinKind;
  source: CheckinSource;
  reason?: BlockedReason | null;
  inputValue?: string | null;
  recordedAt: string;
  deviceId: string;
}

const DEVICE_ID_KEY = 'nt-kiosk-device-id';
// 送信待ちと送れなかった記録は、ログイン中のアカウントごとに分けて保存する
// (未送信のままログアウトして別のアカウントでログインしたとき、前の人の記録を
// 新しいアカウントの記録として送らないため。元のアカウントでログインし直すと送られる)。
const QUEUE_KEY = 'nt-kiosk-queue-v1';
const BOOTSTRAP_KEY = 'nt-kiosk-bootstrap-v1';
// サーバが受け付けなかった記録。捨てずに残し、管理者メニューと端末の設定に理由つきで出す
const REJECTED_KEY = 'nt-kiosk-rejected-v1';
const MAX_REJECTED = 100;
const RELOAD_INTERVAL_MS = 60 * 1000;
// 回線が切れているとき、通信が返ってこないまま止まらないようにする
const REQUEST_TIMEOUT_MS = 10 * 1000;
// これより長く送れていない記録があれば「送信待ち」を出す
const PENDING_WARN_MS = 5 * 1000;
// 1 回の送信で送る記録の上限 (サーバの上限と同じ)。残りは続けて送る
const MAX_RECORDS_PER_REQUEST = 40;
const FLUSH_INTERVAL_MS = 10 * 1000;

// crypto.randomUUID は https でないと使えない (Tailscale 経由の http で開発サーバを
// 開いたときなど)。getRandomValues は http でも使えるので、それで UUID v4 を作る。
export function newUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = newUuid();
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

function readQueue(key: string): PendingRecord[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(key: string, queue: PendingRecord[]) {
  localStorage.setItem(key, JSON.stringify(queue));
}

export interface RejectedRecord extends PendingRecord {
  rejectReason: string;
}

export const REJECT_REASON_LABEL: Record<string, string> = {
  unknown_exhibitor: '出展者が見つからない',
  invalid_time: '端末の時刻がずれている',
  off_day: '会期外',
  not_paid: '支払いが確認できない',
  not_exhibiting_day: '出展日ではない',
  invalid_record: '記録の形式が正しくない',
  input_mismatch: '入力した番号と出展者が一致しない',
  already_checked_in: 'すでに受付済み (または本日は受付不要)',
};

function readRejected(key: string): RejectedRecord[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readCachedBootstrap(key: string): KioskBootstrap | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as KioskBootstrap) : null;
  } catch {
    return null;
  }
}

export type ConnectionState = 'online' | 'offline';

// accountId: ログイン中のアカウント (bauth_user.id)。送信待ちの保存先を分けるのに使う
export function useKioskData(accountId: string) {
  const queueKey = `${QUEUE_KEY}:${accountId}`;
  const rejectedKey = `${REJECTED_KEY}:${accountId}`;
  // 一覧の控えもアカウントごと (別のアカウントでログインし直したとき、前の人のデータを出さない)
  const bootstrapKey = `${BOOTSTRAP_KEY}:${accountId}`;
  const [data, setData] = useState<KioskBootstrap | null>(() => readCachedBootstrap(bootstrapKey));
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('online');
  const [queue, setQueue] = useState<PendingRecord[]>(() => readQueue(queueKey));
  const [rejectedRecords, setRejectedRecords] = useState<RejectedRecord[]>(() => readRejected(rejectedKey));
  const [authError, setAuthError] = useState(false);
  // サーバとの時計のずれ (サーバ − 端末)。受付日と記録時刻をサーバの時計に合わせる。
  const [clockOffset, setClockOffset] = useState(0);
  const flushing = useRef(false);
  // 送信中に次の送信を頼まれたら、終わったあとにもう一度送る
  const flushAgain = useRef(false);
  // 読み直しは定期のものと送信後のものが重なることがある。古い応答があとから届いて
  // 新しいデータを上書きしないよう、始めた順の番号を振り、適用済みより古い応答は捨てる。
  const reloadSeq = useRef(0);
  const appliedSeq = useRef(0);
  const deviceId = useMemo(() => getDeviceId(), []);

  // 読み込めたら true
  const reload = useCallback(async (): Promise<boolean> => {
    const seq = ++reloadSeq.current;
    try {
      const before = Date.now();
      const res = await api.get<{ data: KioskBootstrap }>('/api/kiosk/bootstrap', {
        timeout: REQUEST_TIMEOUT_MS,
      });
      const after = Date.now();
      if (seq < appliedSeq.current) return true; // もっと新しい読み直しが先に反映済み
      appliedSeq.current = seq;
      const body = res.data.data;
      setClockOffset(new Date(body.serverNow).getTime() - (before + after) / 2);
      setData(body);
      setLoadedAt(new Date());
      setConnection('online');
      setAuthError(false);
      localStorage.setItem(bootstrapKey, JSON.stringify(body));
      return true;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403) setAuthError(true);
      else setConnection('offline');
      return false;
    }
  }, [bootstrapKey]);

  const flush = useCallback(async (): Promise<void> => {
    if (flushing.current) {
      flushAgain.current = true;
      return;
    }
    const current = readQueue(queueKey);
    if (current.length === 0) return;
    flushing.current = true;
    try {
      const res = await api.post<{ data: { accepted: string[]; rejected: { id: string; reason: string }[] } }>(
        '/api/kiosk/records',
        { records: current.slice(0, MAX_RECORDS_PER_REQUEST) },
        { timeout: REQUEST_TIMEOUT_MS }
      );
      const done = new Set([...res.data.data.accepted, ...res.data.data.rejected.map((r) => r.id)]);
      if (res.data.data.rejected.length > 0) {
        // 受け付けられなかった記録は捨てずに残す (画面では「受付しました」と出ているので、
        // スタッフが気づいて対応できるようにする)
        const reasonOf = new Map(res.data.data.rejected.map((r) => [r.id, r.reason]));
        const moved = current
          .filter((r) => reasonOf.has(r.id))
          .map((r) => ({ ...r, rejectReason: reasonOf.get(r.id) ?? 'invalid_record' }));
        const nextRejected = [...readRejected(rejectedKey), ...moved].slice(-MAX_REJECTED);
        localStorage.setItem(rejectedKey, JSON.stringify(nextRejected));
        setRejectedRecords(nextRejected);
      }
      // 送れた記録を手元の一覧に反映してから送信待ちから外す。先に外すと、次の読み直し
      // (最大 1 分後) まで受付が消えて見え、同じ出展者をもう一度受付できてしまう。
      // 読み直せなかったら送信待ちに残す (次の送信で送り直しても、サーバは同じ id を二重に入れない)。
      if (!(await reload())) return;
      // 送信中に増えた分を消さないよう、読み直してから除く
      const next = readQueue(queueKey).filter((r) => !done.has(r.id));
      writeQueue(queueKey, next);
      setQueue(next);
      setConnection('online');
      // 1 回で送り切れなかった分 (回線断でたまった記録が多いとき) は続けて送る
      if (next.length > 0 && current.length > MAX_RECORDS_PER_REQUEST) flushAgain.current = true;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403) setAuthError(true);
      else if (status === 400) {
        // サーバは記録を 1 件ずつ検証して不正なものだけ rejected で返すので、ここに来るのは
        // リクエスト自体が壊れているとき。受付の記録を失わないよう捨てずに残す
        // (残り続ければ「送信待ち」が出るので、スタッフが気づける)。
        console.error('受付記録の送信で 400', err);
      } else setConnection('offline');
    } finally {
      flushing.current = false;
      if (flushAgain.current) {
        flushAgain.current = false;
        setTimeout(() => flushRef.current(), 0);
      }
    }
  }, [reload, queueKey, rejectedKey]);
  const flushRef = useRef(flush);
  flushRef.current = flush;

  useEffect(() => {
    reload();
    const t = setInterval(reload, RELOAD_INTERVAL_MS);
    return () => clearInterval(t);
  }, [reload]);

  useEffect(() => {
    flush();
    const t = setInterval(flush, FLUSH_INTERVAL_MS);
    const onOnline = () => {
      flush();
      reload();
    };
    window.addEventListener('online', onOnline);
    return () => {
      clearInterval(t);
      window.removeEventListener('online', onOnline);
    };
  }, [flush, reload]);

  const now = useCallback(() => new Date(Date.now() + clockOffset), [clockOffset]);

  // 端末の時計 (サーバに合わせたもの) で今日が何日目か。会期外は null (テスト表示)。
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 15 * 1000);
    return () => clearInterval(t);
  }, []);
  const today = useMemo(() => {
    const d = now();
    const jstDate = toJstDateString(d);
    const day = data?.eventDates ? eventDayOf(jstDate, data.eventDates) : null;
    return { date: d, jstDate, day };
    // tick で 15 秒ごとに日付を見直す
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [now, data?.eventDates, tick]);

  // サーバの記録 + まだ送れていない記録
  const records = useMemo<CheckinRecord[]>(() => {
    const server = data?.records ?? [];
    const serverIds = new Set(server.map((r) => r.id));
    const local: CheckinRecord[] = [];
    for (const q of queue) {
      if (serverIds.has(q.id) || !data?.eventDates) continue;
      const day = eventDayOf(toJstDateString(new Date(q.recordedAt)), data.eventDates);
      if (!day) continue;
      // 送信待ちはログイン中のアカウントの分だけ (保存先をアカウントごとに分けている)
      local.push({ ...q, eventDay: day, cancelledAt: null, recordedBy: accountId });
    }
    return [...server, ...local];
  }, [data, queue, accountId]);

  const pendingIds = useMemo(() => new Set(queue.map((q) => q.id)), [queue]);

  const clearRejected = useCallback(() => {
    localStorage.removeItem(rejectedKey);
    setRejectedRecords([]);
  }, [rejectedKey]);

  // 送れていない記録があるか。送れた記録も読み直しが終わるまで一瞬キューに残るので、
  // 通信できないと分かったときか、一定時間送れていないときだけ true にする。
  const [stalePending, setStalePending] = useState(false);
  useEffect(() => {
    const check = () => {
      const t = Date.now() + clockOffset;
      setStalePending(queue.some((q) => t - new Date(q.recordedAt).getTime() > PENDING_WARN_MS));
    };
    check();
    const timer = setInterval(check, 1000);
    return () => clearInterval(timer);
  }, [queue, clockOffset]);
  const showPending = queue.length > 0 && (connection === 'offline' || stalePending);

  const addRecord = useCallback(
    // recordedAt を省略すると今の時刻。「受付不要」は表示した時刻で残すので指定できるようにしている
    (rec: Omit<PendingRecord, 'id' | 'recordedAt' | 'deviceId'> & { recordedAt?: string }) => {
      const full: PendingRecord = {
        ...rec,
        id: newUuid(),
        recordedAt: rec.recordedAt ?? now().toISOString(),
        deviceId,
      };
      const next = [...readQueue(queueKey), full];
      writeQueue(queueKey, next);
      setQueue(next);
      // すぐ送る (失敗したら 10 秒ごとの再送に任せる)
      setTimeout(() => flush(), 0);
      return full;
    },
    [deviceId, flush, now, queueKey]
  );

  const cancelRecord = useCallback(
    async (id: string) => {
      await api.post(`/api/kiosk/records/${id}/cancel`);
      await reload();
    },
    [reload]
  );

  return {
    data,
    loadedAt,
    connection,
    authError,
    queueLength: queue.length,
    showPending,
    rejectedRecords,
    clearRejected,
    pendingIds,
    deviceId,
    today,
    now,
    records,
    reload,
    flush,
    addRecord,
    cancelRecord,
  };
}

export type KioskData = ReturnType<typeof useKioskData>;

export function useDayView(kiosk: KioskData, day: EventDay) {
  return useMemo(() => {
    const states = computeDayStates(kiosk.data?.exhibitors ?? [], kiosk.records, kiosk.data?.dayStatuses ?? [], day);
    const byId = new Map(states.map((s) => [s.exhibitor.id, s]));
    return { states, byId, summary: summarizeDay(states) };
  }, [kiosk.data, kiosk.records, day]);
}

// 「10月3日（土）」
export function formatJstDateLabel(jstDate: string): string {
  const [y, m, d] = jstDate.split('-').map((v) => parseInt(v, 10));
  const wd = ['日', '月', '火', '水', '木', '金', '土'][new Date(Date.UTC(y!, m! - 1, d!)).getUTCDay()];
  return `${m}月${d}日（${wd}）`;
}

// 「9:12」
export function formatJstTime(iso: string | Date): string {
  const d = new Date(new Date(iso).getTime() + 9 * 60 * 60 * 1000);
  return `${d.getUTCHours()}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

// 「10/3 9:12」
export function formatJstDateTime(iso: string): string {
  const d = new Date(new Date(iso).getTime() + 9 * 60 * 60 * 1000);
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${formatJstTime(iso)}`;
}
